import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  HolderOutlined,
  InboxOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SettingOutlined,
  SwapOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  List,
  Modal,
  Row,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import type { CurrentUser } from '@cell-bank/shared';
import type { DragEvent, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatTenantDateTime } from '../lib/tenant';

type Transaction = {
  id: string;
  operationType: string;
  executedAt: string;
  executedBy: string;
  executedByName: string | null;
  items: Array<{ cellLineCode: string }>;
};

type SampleListResponse = {
  statusCounts?: Record<string, number>;
  cellLineCount?: number;
};

type InventoryRequest = {
  id: string;
  requestNo: string;
  operationType: string;
  status: 'PENDING' | 'APPROVED';
  title: string;
  createdAt: string;
};

type TodoItem = {
  id: string;
  kind: 'approval' | 'execution';
  title: string;
  createdAt: string;
};

type ShortcutGroup = 'admin' | 'manager' | 'user';
type ShortcutKey =
  | 'newSample'
  | 'newUser'
  | 'samples'
  | 'cellLines'
  | 'materials'
  | 'storage'
  | 'requests'
  | 'approvals'
  | 'transactions'
  | 'quality'
  | 'search'
  | 'notifications'
  | 'users'
  | 'audit'
  | 'system';

type ShortcutOption = {
  key: ShortcutKey;
  path: string;
  icon: ReactNode;
  label: string;
};

type GreetingPeriod = 'morning' | 'afternoon' | 'evening';

function greetingPeriodForTimezone(
  timeZone?: string | null,
  date = new Date(),
): GreetingPeriod {
  const fallbackTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Shanghai';
  const hourPart = new Intl.DateTimeFormat('zh-CN', {
    hour: 'numeric',
    hour12: false,
    timeZone: timeZone || fallbackTimeZone,
  })
    .formatToParts(date)
    .find((part) => part.type === 'hour')?.value;
  const hour = Number(hourPart) % 24;
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  return 'evening';
}

const defaultShortcuts: Record<ShortcutGroup, ShortcutKey[]> = {
  admin: [
    'newSample',
    'newUser',
    'approvals',
    'transactions',
    'audit',
    'system',
  ],
  manager: [
    'newSample',
    'samples',
    'cellLines',
    'storage',
    'requests',
    'transactions',
    'quality',
  ],
  user: ['samples', 'cellLines', 'requests', 'search', 'notifications'],
};

function shortcutGroup(user: CurrentUser): ShortcutGroup {
  if (user.permissions.includes('identity:read')) return 'admin';
  if (
    user.permissions.includes('inventory:execute') ||
    user.permissions.includes('sample:create')
  )
    return 'manager';
  return 'user';
}

export function WorkplacePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [messageApi, messageHolder] = message.useMessage();
  const [currentUser, setCurrentUser] = useState<CurrentUser>();
  const [mySampleCount, setMySampleCount] = useState<number | null>(null);
  const [myCellLineCount, setMyCellLineCount] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [selectedShortcutKeys, setSelectedShortcutKeys] = useState<
    ShortcutKey[]
  >([]);
  const [draftShortcutKeys, setDraftShortcutKeys] = useState<ShortcutKey[]>([]);
  const [shortcutConfigOpen, setShortcutConfigOpen] = useState(false);
  const [savingShortcuts, setSavingShortcuts] = useState(false);
  const [draggedShortcut, setDraggedShortcut] = useState<ShortcutKey>();
  const [unavailable, setUnavailable] = useState(false);
  const [greetingPeriod, setGreetingPeriod] = useState<GreetingPeriod>(() =>
    greetingPeriodForTimezone(),
  );

  useEffect(() => {
    const timeZone = currentUser?.tenant.timezone;
    setGreetingPeriod(greetingPeriodForTimezone(timeZone));
    const timer = window.setInterval(() => {
      setGreetingPeriod(greetingPeriodForTimezone(timeZone));
    }, 60 * 1_000);
    return () => window.clearInterval(timer);
  }, [currentUser?.tenant.timezone]);

  useEffect(() => {
    const load = async () => {
      const me = await fetch('/api/v1/auth/me', { credentials: 'include' });
      if (me.status === 401) {
        window.location.assign('/login');
        return;
      }
      if (!me.ok) {
        setUnavailable(true);
        return;
      }

      const user = (await me.json()) as CurrentUser;
      setCurrentUser(user);
      const canReadSamples = user.permissions.includes('sample:read');
      const canReadHistory = user.permissions.includes('audit:read');
      const canReadActionableRequests = user.permissions.some((permission) =>
        ['inventory:approve', 'inventory:execute'].includes(permission),
      );
      const [samples, history, actionableRequests] = await Promise.allSettled([
        canReadSamples
          ? fetch(
              `/api/v1/samples?operatorId=${encodeURIComponent(user.id)}&limit=10000`,
              { credentials: 'include' },
            )
          : Promise.resolve(null),
        canReadHistory
          ? fetch('/api/v1/inventory-transactions?limit=6', {
              credentials: 'include',
            })
          : Promise.resolve(null),
        canReadActionableRequests
          ? fetch('/api/v1/inventory-requests?relation=ACTIONABLE', {
              credentials: 'include',
            })
          : Promise.resolve(null),
      ]);

      const responses = [samples, history, actionableRequests];
      if (
        responses.some(
          (result) =>
            result.status === 'fulfilled' && result.value?.status === 401,
        )
      ) {
        window.location.assign('/login');
        return;
      }
      setUnavailable(responses.some((result) => result.status === 'rejected'));
      if (samples.status === 'fulfilled' && samples.value?.ok) {
        const data = (await samples.value.json()) as SampleListResponse;
        setMySampleCount(
          Object.values(data.statusCounts ?? {}).reduce(
            (total, count) => total + count,
            0,
          ),
        );
        setMyCellLineCount(data.cellLineCount ?? 0);
      }
      if (history.status === 'fulfilled' && history.value?.ok) {
        setTransactions(
          ((await history.value.json()) as { items: Transaction[] }).items,
        );
      }
      const nextTodos: TodoItem[] = [];
      if (
        actionableRequests.status === 'fulfilled' &&
        actionableRequests.value?.ok
      ) {
        const data = (await actionableRequests.value.json()) as {
          items: InventoryRequest[];
        };
        nextTodos.push(
          ...data.items.map((item) => ({
            id: `request:${item.id}`,
            kind:
              item.status === 'PENDING'
                ? ('approval' as const)
                : ('execution' as const),
            title: item.title || item.requestNo,
            createdAt: item.createdAt,
          })),
        );
      }
      setTodos(
        nextTodos
          .sort(
            (left, right) =>
              new Date(right.createdAt).valueOf() -
              new Date(left.createdAt).valueOf(),
          )
          .slice(0, 10),
      );
    };
    void load();
  }, [t]);

  const permissions = currentUser?.permissions ?? [];
  const can = (permission: string) => permissions.includes(permission);
  const canSearch =
    can('sample:read') || can('cell_line:read') || can('audit:read');
  const canQuality = can('qc:read') || can('qc:manage');
  const displayName = currentUser?.displayName ?? currentUser?.username ?? '';
  const shortcutOptions = useMemo<ShortcutOption[]>(
    () =>
      [
        can('sample:create') && {
          key: 'newSample' as const,
          path: '/samples',
          icon: <InboxOutlined />,
          label: t('workplace.quickLinks.newSample'),
        },
        can('sample:read') && {
          key: 'samples' as const,
          path: '/samples',
          icon: <InboxOutlined />,
          label: t('workplace.quickLinks.samples'),
        },
        can('cell_line:read') && {
          key: 'cellLines' as const,
          path: '/cell-lines',
          icon: <ExperimentOutlined />,
          label: t('workplace.quickLinks.cellLines'),
        },
        can('cell_line:read') && {
          key: 'materials' as const,
          path: '/materials',
          icon: <SafetyCertificateOutlined />,
          label: t('workplace.quickLinks.materials'),
        },
        can('storage:manage') && {
          key: 'storage' as const,
          path: '/storage',
          icon: <DatabaseOutlined />,
          label: t('workplace.quickLinks.storage'),
        },
        can('inventory:request') && {
          key: 'requests' as const,
          path: '/requests',
          icon: <SwapOutlined />,
          label: t('workplace.quickLinks.requests'),
        },
        can('inventory:approve') && {
          key: 'approvals' as const,
          path: '/approvals',
          icon: <CheckCircleOutlined />,
          label: t('workplace.quickLinks.approvals'),
        },
        can('audit:read') && {
          key: 'transactions' as const,
          path: '/transactions',
          icon: <SwapOutlined />,
          label: t('workplace.quickLinks.transactions'),
        },
        canQuality && {
          key: 'quality' as const,
          path: '/quality',
          icon: <SafetyCertificateOutlined />,
          label: t('workplace.quickLinks.quality'),
        },
        canSearch && {
          key: 'search' as const,
          path: '/search',
          icon: <SearchOutlined />,
          label: t('workplace.quickLinks.search'),
        },
        can('notification:read') && {
          key: 'notifications' as const,
          path: '/notifications',
          icon: <InboxOutlined />,
          label: t('workplace.quickLinks.notifications'),
        },
        can('identity:read') && {
          key: 'users' as const,
          path: '/users',
          icon: <UserOutlined />,
          label: t('workplace.quickLinks.users'),
        },
        can('identity:manage') && {
          key: 'newUser' as const,
          path: '/users',
          icon: <UserOutlined />,
          label: t('workplace.quickLinks.newUser'),
        },
        can('audit:read') && {
          key: 'audit' as const,
          path: '/audit',
          icon: <SafetyCertificateOutlined />,
          label: t('workplace.quickLinks.audit'),
        },
        can('identity:read') && {
          key: 'system' as const,
          path: '/system',
          icon: <SettingOutlined />,
          label: t('workplace.quickLinks.system'),
        },
      ].filter(Boolean) as ShortcutOption[],
    [canSearch, canQuality, permissions, t],
  );
  const shortcutOptionMap = useMemo(
    () => new Map(shortcutOptions.map((option) => [option.key, option])),
    [shortcutOptions],
  );
  const group = currentUser ? shortcutGroup(currentUser) : 'user';

  useEffect(() => {
    if (!currentUser) return;
    const availableKeys = new Set(shortcutOptions.map((option) => option.key));
    const initialKeys =
      currentUser.workplaceShortcuts ??
      defaultShortcuts[shortcutGroup(currentUser)];
    setSelectedShortcutKeys(
      initialKeys
        .filter(
          (key): key is ShortcutKey =>
            typeof key === 'string' && availableKeys.has(key as ShortcutKey),
        )
        .slice(0, 6),
    );
  }, [currentUser, shortcutOptions]);

  const operationLabel = (value: string) => {
    const key = `transactions.operationTypes.${value}`;
    const translated = t(key);
    return translated === key ? value : translated;
  };
  const openShortcutConfig = () => {
    setDraftShortcutKeys(selectedShortcutKeys);
    setShortcutConfigOpen(true);
  };
  const toggleShortcut = (key: ShortcutKey, checked: boolean) => {
    if (checked) {
      if (draftShortcutKeys.length >= 6) {
        void messageApi.warning(t('workplace.shortcuts.maxReached'));
        return;
      }
      setDraftShortcutKeys((current) => [...current, key]);
      return;
    }
    setDraftShortcutKeys((current) => current.filter((item) => item !== key));
  };
  const moveShortcut = (targetKey: ShortcutKey) => {
    if (!draggedShortcut || draggedShortcut === targetKey) return;
    setDraftShortcutKeys((current) => {
      const fromIndex = current.indexOf(draggedShortcut);
      const toIndex = current.indexOf(targetKey);
      if (fromIndex < 0 || toIndex < 0) return current;
      const next = [...current];
      next.splice(fromIndex, 1);
      next.splice(toIndex, 0, draggedShortcut);
      return next;
    });
    setDraggedShortcut(undefined);
  };
  const saveShortcuts = async () => {
    setSavingShortcuts(true);
    try {
      const response = await fetch('/api/v1/auth/me/workplace-shortcuts', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ shortcuts: draftShortcutKeys }),
      });
      if (!response.ok) throw new Error(t('workplace.shortcuts.saveFailed'));
      const user = (await response.json()) as CurrentUser;
      setCurrentUser(user);
      setShortcutConfigOpen(false);
      await messageApi.success(t('workplace.shortcuts.saved'));
    } catch (error) {
      await messageApi.error(
        error instanceof Error
          ? error.message
          : t('workplace.shortcuts.saveFailed'),
      );
    } finally {
      setSavingShortcuts(false);
    }
  };
  const displayShortcutOptions = selectedShortcutKeys
    .map((key) => shortcutOptionMap.get(key))
    .filter((option): option is ShortcutOption => Boolean(option));
  const configShortcutOptions = [
    ...draftShortcutKeys
      .map((key) => shortcutOptionMap.get(key))
      .filter((option): option is ShortcutOption => Boolean(option)),
    ...shortcutOptions.filter(
      (option) => !draftShortcutKeys.includes(option.key),
    ),
  ];

  return (
    <section className="dashboard-workplace">
      {messageHolder}
      {unavailable && (
        <Card className="dashboard-workplace-notice" size="small">
          <Space>
            <WarningOutlined />
            <span>{t('workplace.partialData')}</span>
          </Space>
        </Card>
      )}

      <div className="dashboard-workplace-hero">
        <Typography.Title level={4} className="dashboard-workplace-heading">
          {t('workplace.title')}
        </Typography.Title>
        <div className="dashboard-workplace-hero-body">
          <div className="dashboard-workplace-profile">
            <Avatar
              className="dashboard-workplace-avatar"
              size={72}
              icon={<UserOutlined />}
            />
            <div>
              <Typography.Title level={4} className="dashboard-workplace-title">
                {t(`workplace.greeting.${greetingPeriod}`, {
                  name: displayName,
                })}
              </Typography.Title>
              <Typography.Text type="secondary">
                {t(`workplace.shortcuts.groups.${group}`)}
              </Typography.Text>
            </div>
          </div>
          <div className="dashboard-workplace-hero-stats">
            <Button
              type="link"
              className="dashboard-workplace-stat-link"
              disabled={!can('sample:read')}
              onClick={() =>
                void navigate(
                  '/samples?operatorId=' +
                    encodeURIComponent(currentUser?.id ?? ''),
                )
              }
            >
              <Typography.Text type="secondary">
                {t('workplace.stats.mySamples')}
              </Typography.Text>
              <Typography.Text className="dashboard-workplace-hero-stat-value">
                {mySampleCount ?? '-'}
              </Typography.Text>
            </Button>
            <Button
              type="link"
              className="dashboard-workplace-stat-link"
              disabled={!can('cell_line:read')}
              onClick={() =>
                void navigate(
                  '/cell-lines?operatorId=' +
                    encodeURIComponent(currentUser?.id ?? ''),
                )
              }
            >
              <Typography.Text type="secondary">
                {t('workplace.stats.myCellLines')}
              </Typography.Text>
              <Typography.Text className="dashboard-workplace-hero-stat-value">
                {myCellLineCount ?? '-'}
              </Typography.Text>
            </Button>
          </div>
        </div>
      </div>

      <Row gutter={[16, 16]} className="dashboard-workplace-content-grid">
        <Col xs={24} lg={16} className="dashboard-workplace-main-column">
          <Card
            title={t('workplace.todos.title')}
            extra={
              can('inventory:approve') || can('inventory:execute') ? (
                <Button type="link" onClick={() => void navigate('/approvals')}>
                  {t('workplace.todos.viewAll')} <ArrowRightOutlined />
                </Button>
              ) : null
            }
            className="dashboard-workplace-todos-card"
          >
            <List
              dataSource={todos}
              locale={{ emptyText: t('workplace.todos.empty') }}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button
                      key="handle"
                      type="link"
                      onClick={() => void navigate('/approvals')}
                    >
                      {t('workplace.todos.handle')}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space wrap>
                        <Tag color={item.kind === 'approval' ? 'gold' : 'blue'}>
                          {t(`workplace.todos.kinds.${item.kind}`)}
                        </Tag>
                        <Typography.Text>{item.title}</Typography.Text>
                      </Space>
                    }
                    description={formatTenantDateTime(item.createdAt)}
                  />
                </List.Item>
              )}
            />
          </Card>
          <Card
            title={t('workplace.activity')}
            extra={
              <Button
                type="link"
                onClick={() => void navigate('/transactions')}
              >
                {t('workplace.viewAll')} <ArrowRightOutlined />
              </Button>
            }
            className="dashboard-workplace-activity-card"
          >
            <List
              itemLayout="horizontal"
              dataSource={transactions}
              locale={{ emptyText: t('workplace.noActivity') }}
              renderItem={(item) => {
                const cellLines = [
                  ...new Set(item.items.map((entry) => entry.cellLineCode)),
                ];
                return (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar className="dashboard-workplace-activity-avatar">
                          <SwapOutlined />
                        </Avatar>
                      }
                      title={
                        <Space wrap>
                          <Tag color="blue">
                            {operationLabel(item.operationType)}
                          </Tag>
                          <Typography.Text>
                            {cellLines.slice(0, 3).join('、') ||
                              t('workplace.unnamedActivity')}
                          </Typography.Text>
                        </Space>
                      }
                      description={
                        <Space size={12} wrap>
                          <span>{item.executedByName ?? item.executedBy}</span>
                          <span>{formatTenantDateTime(item.executedAt)}</span>
                        </Space>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            title={t('workplace.quickLinks.title')}
            extra={
              <Button
                type="text"
                icon={<SettingOutlined />}
                aria-label={t('workplace.shortcuts.configure')}
                title={t('workplace.shortcuts.configure')}
                onClick={openShortcutConfig}
              />
            }
            className="dashboard-workplace-quick-links-card"
          >
            <div className="dashboard-workplace-quick-links">
              {displayShortcutOptions.length ? (
                displayShortcutOptions.map((option) => (
                  <Button
                    key={option.key}
                    type="text"
                    className="dashboard-workplace-quick-link"
                    onClick={() => {
                      if (option.key === 'newSample') {
                        void navigate(option.path, {
                          state: { createSample: true },
                        });
                        return;
                      }
                      void navigate(
                        option.key === 'newUser'
                          ? '/users?action=create'
                          : option.path,
                      );
                    }}
                  >
                    <span className="dashboard-workplace-quick-link-icon">
                      {option.icon}
                    </span>
                    <Typography.Text strong>{option.label}</Typography.Text>
                    <ArrowRightOutlined />
                  </Button>
                ))
              ) : (
                <Typography.Text type="secondary">
                  {t('workplace.shortcuts.empty')}
                </Typography.Text>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Modal
        title={t('workplace.shortcuts.title')}
        open={shortcutConfigOpen}
        onCancel={() => setShortcutConfigOpen(false)}
        onOk={() => void saveShortcuts()}
        okButtonProps={{ disabled: draftShortcutKeys.length > 6 }}
        confirmLoading={savingShortcuts}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
      >
        <Typography.Paragraph type="secondary">
          {t('workplace.shortcuts.hint', {
            group: t(`workplace.shortcuts.groups.${group}`),
            max: 6,
          })}
        </Typography.Paragraph>
        <div className="dashboard-shortcut-config-list">
          {configShortcutOptions.map((option) => {
            const selected = draftShortcutKeys.includes(option.key);
            const onDragStart = (event: DragEvent<HTMLDivElement>) => {
              if (!selected) return;
              event.dataTransfer.effectAllowed = 'move';
              setDraggedShortcut(option.key);
            };
            return (
              <div
                key={option.key}
                className={`dashboard-shortcut-config-row${selected ? ' is-selected' : ''}`}
                draggable={selected}
                onDragStart={onDragStart}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => moveShortcut(option.key)}
              >
                <Checkbox
                  checked={selected}
                  onChange={(event) =>
                    toggleShortcut(option.key, event.target.checked)
                  }
                >
                  <Space size={8}>
                    <span className="dashboard-shortcut-config-icon">
                      {option.icon}
                    </span>
                    {option.label}
                  </Space>
                </Checkbox>
                {selected && (
                  <HolderOutlined
                    className="dashboard-shortcut-config-drag-handle"
                    aria-label={t('workplace.shortcuts.dragToSort')}
                  />
                )}
              </div>
            );
          })}
        </div>
        <Typography.Text
          type="secondary"
          className="dashboard-shortcut-config-count"
        >
          {t('workplace.shortcuts.selectedCount', {
            count: draftShortcutKeys.length,
            max: 6,
          })}
        </Typography.Text>
      </Modal>
    </section>
  );
}
